import React, { useEffect, useMemo, useState } from "react";
import { FaPlus, FaStore, FaUserTie } from "react-icons/fa";
import { Alert } from "@mui/material";
import useAuth from "../useAuth";
import usePermission from "../usePermission";
import { useBelowFirmsData } from "../useBelowFirmsData";
import useFirmBusy from "../dealerRoles/useFirmBusy";
import {
  useSearchUnlinkedFirmsMutation,
  useLinkSubFirmMutation,
  useUnlinkSubFirmMutation,
  useSendSubFirmQuotaMutation,
  useLazyGetSendableSubFirmQuotaQuery,
} from "../../store";
import ConfirmationModal from "../../Components/Firm/Tabs/ConfirmationModal";
import AddFirmDrawer from "../../Components/Firm/Tabs/AddFirmDrawer";
import {
  useLazySearchFirmsForDropdownQuery,
  useLazyGetFirmByIDQuery,
} from "../../store/storageRoles/firmApi";
import SubFirmQuotaDrawer from "../../Components/Firm/Tabs/SubFirmQuotaDrawer";
import { firmProductQuotaSummary } from "../../Components/Firm/Tabs/QuotaSplitCards";

/**
 * ALT FİRMA İŞLEMLERİ (#324) — eskiden yalnız "Alt Firmalar" sekmesinin
 * (SubFirmsTab) içindeydi; merkezde Kota & Cihazlar sayfasının firma grup
 * satırları da aynı işlemleri kullanır. Davranış sekmedekiyle birebir:
 *  - satırlar: alt firmalar / alt bayiler (+ bayide müşteriler, `customersSource`)
 *  - Alt Firma Ekle (arama + onay), Bağı Kopar (onay), Kota Ver / Kota Al drawer'ı
 *  - meşguliyet (#303): üst firma meşgulse tüm satırlar kilitli
 *  - bağla / kopar sonrası satır ANINDA eklenir / düşer (sunucu listesi örtüyü temizler)
 *
 * Bayi paneli SubFirmsTab'ı `externalData` / `externalHooks` ile kullanır — aynen geçer.
 *
 * customersSource: { customers, loading, refetch, canSendQuota, canUnlink,
 *   unlink(customer) → Promise, unlinkLoading, limitToPool, requireLiveSource }
 *
 * Dönen `renderLayer()` uyarıyı, onay modallarını ve kota drawer'ını çizer;
 * `renderLinkButton()` "Alt Firma Ekle" düğmesini ve arama listesini.
 */
export default function useSubFirmActions({
  firm,
  externalData,
  externalHooks,
  customersSource = null,
  includeSubDealers = true,
  onChanged,
  // false → hiçbir istek atılmaz (alt firma / müşteri izni olmayan kullanıcı;
  // sendableSubFirmQuota firms.belowFirms | firms.customers ister, #414)
  enabled = true,
}) {
  const { auth } = useAuth();
  const { hasPermission } = usePermission();
  const [alert, setAlert] = useState(null);
  const isDealerFirm = firm?.firmType === "dealer";
  // Bayi kuralları (#303): Kota Ver yalnız havuzdan, Kota Al canlı okumalı.
  const isDealerUser = auth?.role === "dealer";

  // Ekleme paneli (#326): null | "sub" (alt firma / alt bayi) | "customer"
  const [addMode, setAddMode] = useState(null);
  const [confirmUnlink, setConfirmUnlink] = useState(null);
  // { firm, direction: "toSub" | "toParent" }
  const [quotaDrawer, setQuotaDrawer] = useState(null);

  const canLink = enabled && hasPermission("firms.belowFirmsLink");
  // Bayiye müşteri bağlama (#326) — bayi bağı uçuyla, Bağı Kopar ile aynı izin
  const canLinkCustomer = enabled && !!customersSource?.canLink;
  const [searchFirms] = useLazySearchFirmsForDropdownQuery();
  const [getFirmDetail] = useLazyGetFirmByIDQuery();
  const canUnlink = hasPermission("firms.belowFirmsUnlink");
  const canSendQuota = hasPermission(
    auth?.role === "dealer" ? "dealers.sendSubFirmQuota" : "firms.sendSubFirmQuota",
  );

  const internalData = useBelowFirmsData(
    externalData || !includeSubDealers || !enabled
      ? { id: null, token: null }
      : { id: firm?._id, token: auth.accessToken },
  );
  const subDealerData = includeSubDealers ? externalData || internalData : {};
  const belowFirms = subDealerData.belowFirms;
  const belowFirmsIsLoading = !!subDealerData.belowFirmsIsLoading || !!customersSource?.loading;
  const belowFirmsIsFetching = !!subDealerData.belowFirmsIsFetching;

  // Bayide müşteri satırları işaretlenir (#376)
  const customerRows = useMemo(
    () => (customersSource?.customers || []).map((c) => ({ ...c, __row: "customer" })),
    [customersSource?.customers],
  );
  const isCustomerRow = (row) => row?.__row === "customer";
  const canSendCustomerQuota = !!customersSource?.canSendQuota;
  const canUnlinkCustomer = !!customersSource?.canUnlink;

  const [searchUnlinkedInt] = useSearchUnlinkedFirmsMutation();
  const [linkSubFirmInt] = useLinkSubFirmMutation();
  const [unlinkSubFirmInt, { isLoading: unlinkLoadingInt }] = useUnlinkSubFirmMutation();
  const [sendQuotaInt, { isLoading: sendQuotaLoadingInt }] = useSendSubFirmQuotaMutation();
  const [fetchSendableInt, { data: sendableInt, isFetching: sendableFetchingInt }] =
    useLazyGetSendableSubFirmQuotaQuery();

  const searchUnlinked = externalHooks?.searchUnlinked || searchUnlinkedInt;
  const linkSubFirm = externalHooks?.linkSubFirm || linkSubFirmInt;
  const unlinkSubFirm = externalHooks?.unlinkSubFirm || unlinkSubFirmInt;
  const unlinkLoading = externalHooks?.unlinkLoading ?? unlinkLoadingInt;
  const sendQuota = externalHooks?.sendQuota || sendQuotaInt;
  const sendQuotaLoading = externalHooks?.sendQuotaLoading ?? sendQuotaLoadingInt;
  const fetchSendable = externalHooks?.fetchSendable || fetchSendableInt;
  const sendableResponse = externalHooks?.sendable ?? sendableInt;
  const sendableFetching = externalHooks?.sendableFetching ?? sendableFetchingInt;

  // Firmanın KENDİ ürün kota özeti — "Kota Ver" drawer'ına gönderilebilir bakiye
  const sendableQuota = useMemo(() => sendableResponse?.data || [], [sendableResponse]);

  const refreshSendable = () => {
    if (enabled && firm?._id && auth?.accessToken) fetchSendable({ id: firm._id, token: auth.accessToken });
  };

  useEffect(() => {
    refreshSendable();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firm?._id, auth?.accessToken, enabled]);

  // Bağ koparma/kurma sonrası liste ANINDA güncellenir
  const [removedIds, setRemovedIds] = useState([]);
  const [addedFirms, setAddedFirms] = useState([]);

  useEffect(() => {
    const serverIds = new Set([...(belowFirms || []), ...customerRows].map((f) => f._id));
    setRemovedIds((prev) => {
      const next = prev.filter((id) => serverIds.has(id));
      return next.length === prev.length ? prev : next;
    });
    setAddedFirms((prev) => {
      const next = prev.filter((f) => !serverIds.has(f._id));
      return next.length === prev.length ? prev : next;
    });
  }, [belowFirms, customerRows]);

  const visibleFirms = useMemo(() => {
    const subDealerIds = new Set((belowFirms || []).map((f) => String(f._id)));
    return [
      ...(belowFirms || []).filter((f) => !removedIds.includes(f._id)),
      ...addedFirms,
      // Müşteriler alt bayilerin ardından; aynı firma iki kez listelenmez
      ...customerRows.filter((c) => !subDealerIds.has(String(c._id)) && !removedIds.includes(c._id)),
    ];
  }, [belowFirms, addedFirms, customerRows, removedIds]);

  // Firma meşguliyeti (#303) — üst firma meşgulse tüm satırlar da kilitli
  const { busyIds: rawBusyIds, isBusy } = useFirmBusy([
    firm?._id,
    ...(visibleFirms || []).map((f) => f._id),
  ]);
  const parentBusy = isBusy(firm?._id);
  const busyIds = parentBusy
    ? new Set((visibleFirms || []).map((f) => String(f._id)))
    : rawBusyIds;

  useEffect(() => {
    if (!alert) return undefined;
    const timer = setTimeout(() => setAlert(null), 5000);
    return () => clearTimeout(timer);
  }, [alert]);

  // Alt firma / alt bayi bağla — panelden; hata panelde gösterilsin diye fırlatılır
  const handleLinkFirm = async (subFirm) => {
    await linkSubFirm({ token: auth.accessToken, subFirmId: subFirm._id, parentFirmId: firm._id }).unwrap();
    setAlert({
      severity: "success",
      message: `${subFirm.firmName} ${isDealerFirm ? "alt bayi" : "alt firma"} olarak bağlandı`,
    });
    setAddedFirms((prev) => [...prev, subFirm]);
    onChanged?.();
  };

  // Bayiye müşteri bağla (#326)
  const handleLinkCustomer = async (customer) => {
    await customersSource.link(customer);
    setAlert({ severity: "success", message: `${customer.firmName} müşteri olarak bağlandı` });
    customersSource.refetch?.();
    refreshSendable();
    onChanged?.();
  };

  // Müşteri adayını denetle: ana firması varsa bağlanamaz (tek üst firma,
  // firm-list-tree v1.1/1.2); zaten bu bayideyse gerek yok; başka bayideyse
  // o bayiden ayrılır (panel tek bayi gönderir).
  const inspectCustomer = async (candidate) => {
    const res = await getFirmDetail({ token: auth.accessToken, id: candidate._id }).unwrap();
    const detail = res?.data?.firm || {};
    const dealerId = String(firm._id);
    const dealers = (detail.dealerFirm || []).map((d) => ({
      id: String(d?._id || d),
      name: d?.firmName,
    }));
    if (detail.mainFirm) {
      const mainName = detail.mainFirm?.firmName || "bir ana firma";
      return { block: `Bu firmanın ana firması var (${mainName}); ana firması olan firma bayiye bağlanamaz.` };
    }
    if (dealers.some((d) => d.id === dealerId)) {
      return { block: "Bu firma zaten bu bayinin müşterisi." };
    }
    const others = dealers.filter((d) => d.id !== dealerId);
    if (others.length) {
      const names = others.map((d) => d.name || "başka bir bayi").join(", ");
      return { warning: `Şu an ${names} bayisine bağlı; bağlanınca o bayiden ayrılıp ${firm?.firmName} bayisine geçer.` };
    }
    return { detail: "Firmanın bayisi ya da ana firması yok." };
  };

  const handleUnlinkFirm = async (subFirmId) => {
    // Müşteri satırı: bayi bağını koparır (#202)
    if (confirmUnlink && isCustomerRow(confirmUnlink)) {
      try {
        await customersSource.unlink(confirmUnlink);
        setAlert({ severity: "success", message: `${confirmUnlink.firmName} firmasının bayi bağı koparıldı` });
        setRemovedIds((prev) => [...prev, subFirmId]);
        setConfirmUnlink(null);
        customersSource.refetch?.();
        refreshSendable();
        onChanged?.();
      } catch (err) {
        setAlert({ severity: "error", message: err?.data?.message || "Bayi bağı koparılamadı" });
        setConfirmUnlink(null);
      }
      return;
    }
    try {
      await unlinkSubFirm({ token: auth.accessToken, subFirmId }).unwrap();
      setAlert({ severity: "success", message: "Alt firma başarıyla çıkarıldı" });
      setRemovedIds((prev) => [...prev, subFirmId]);
      setConfirmUnlink(null);
      onChanged?.();
    } catch (err) {
      setAlert({ severity: "error", message: err?.data?.message || "Çıkarma sırasında hata oluştu" });
      setConfirmUnlink(null);
    }
  };

  // GERİ AL yönünde kaynak ALT firmadır; özeti satırın kendi verisinden
  const subFirmQuotaSummary = useMemo(() => firmProductQuotaSummary(quotaDrawer?.firm), [quotaDrawer]);

  const handleTransferQuota = async ({ subFirmId, productName, quota, note, direction }) => {
    try {
      const res = await sendQuota({
        token: auth.accessToken,
        id: firm._id,
        subFirmId,
        productName,
        quota,
        note: note || "",
        direction,
      }).unwrap();
      setAlert({ severity: "success", message: res?.message || "Kota başarıyla aktarıldı" });
      if (isCustomerRow(quotaDrawer?.firm)) customersSource?.refetch?.();
      setQuotaDrawer(null);
      refreshSendable();
      onChanged?.();
    } catch (err) {
      setAlert({ severity: "error", message: err?.data?.message || "Kota aktarılırken hata oluştu" });
    }
  };

  const canSendQuotaFor = (row) => (isCustomerRow(row) ? canSendCustomerQuota : canSendQuota);
  const canUnlinkFor = (row) => (isCustomerRow(row) ? canUnlinkCustomer : canUnlink);
  const linkLabel = isDealerFirm ? "Alt Bayi Ekle" : "Alt Firma Ekle";

  // Ekleme düğmeleri (#326): bayide "Alt Bayi Ekle" + "Müşteri Ekle", diğer
  // firmalarda "Alt Firma Ekle". Akış sağdan açılan panelde (AddFirmDrawer).
  const renderAddButtons = ({ className = "", tones = {} } = {}) => (
    <>
      {canLink && (
        <button type="button" onClick={() => setAddMode("sub")} className={`${className} ${tones.sub || ""}`}>
          {isDealerFirm ? (
            <FaStore className="w-3 h-3 shrink-0 text-violet-600" />
          ) : (
            <FaPlus className="w-3 h-3 shrink-0 text-violet-600" />
          )}
          <span>{linkLabel}</span>
        </button>
      )}
      {canLinkCustomer && (
        <button type="button" onClick={() => setAddMode("customer")} className={`${className} ${tones.customer || ""}`}>
          <FaUserTie className="w-3 h-3 shrink-0 text-teal-600" />
          <span>Müşteri Ekle</span>
        </button>
      )}
    </>
  );
  // Geriye uyum (SubFirmsTab başlığı)
  const renderLinkButton = () =>
    renderAddButtons({
      className:
        "flex items-center gap-1.5 px-3 py-2 bg-indigo-500 text-white rounded-xl text-xs font-semibold hover:bg-indigo-600 transition-colors",
    });

  const renderLayer = () => (
    <>
      {alert && (
        <div className="fixed z-50 left-1/2 top-4 -translate-x-1/2 w-[90%] max-w-md">
          <Alert
            severity={alert.severity}
            onClose={() => setAlert(null)}
            variant="filled"
            sx={{ boxShadow: "0 4px 12px rgba(0,0,0,0.15)", borderRadius: "12px", fontSize: "13px" }}
          >
            {alert.message}
          </Alert>
        </div>
      )}

      <AddFirmDrawer
        open={addMode === "sub"}
        onClose={() => setAddMode(null)}
        title={linkLabel}
        subtitle={`${firm?.firmName || ""} altına bağlanacak firmayı seçin`}
        confirmText="Bağla"
        onSearch={async (q) => {
          const result = await searchUnlinked({ token: auth.accessToken, search: q, parentFirmId: firm._id }).unwrap();
          return result?.data || [];
        }}
        onConfirm={handleLinkFirm}
      />

      {canLinkCustomer && (
        <AddFirmDrawer
          open={addMode === "customer"}
          onClose={() => setAddMode(null)}
          title="Müşteri Ekle"
          subtitle={`${firm?.firmName || ""} bayisine bağlanacak müşteri firmayı seçin`}
          confirmText="Bağla"
          onSearch={async (q) => {
            const res = await searchFirms({ token: auth.accessToken, search: q, firmType: "customer" }).unwrap();
            return res?.data?.firms || [];
          }}
          onInspect={inspectCustomer}
          onConfirm={handleLinkCustomer}
        />
      )}

      <SubFirmQuotaDrawer
        open={!!quotaDrawer}
        onClose={() => setQuotaDrawer(null)}
        onSubmit={handleTransferQuota}
        subFirm={quotaDrawer?.firm}
        direction={quotaDrawer?.direction || "toSub"}
        parentFirmName={firm?.firmName}
        sendableQuota={sendableQuota}
        subFirmQuota={subFirmQuotaSummary}
        summaryLoading={sendableFetching}
        isLoading={sendQuotaLoading}
        limitToPool={isCustomerRow(quotaDrawer?.firm) ? !!customersSource?.limitToPool : isDealerUser}
        requireLiveSource={isCustomerRow(quotaDrawer?.firm) ? !!customersSource?.requireLiveSource : isDealerUser}
      />

      <ConfirmationModal
        open={!!confirmUnlink}
        onClose={() => setConfirmUnlink(null)}
        onConfirm={() => confirmUnlink && handleUnlinkFirm(confirmUnlink._id)}
        title={isCustomerRow(confirmUnlink) ? "Bayi Bağını Kopar" : "Alt Firmayı Çıkar"}
        message={
          !confirmUnlink
            ? ""
            : isCustomerRow(confirmUnlink)
              ? `${confirmUnlink.firmName} firmasının ${firm?.firmName} bayisiyle bağını koparmak istediğinize emin misiniz? Firma bu bayinin müşteri listesinden çıkar; kotası ve makineleri firmada kalır.`
              : `${confirmUnlink.firmName} firmasını alt firma listesinden çıkarmak istediğinize emin misiniz?`
        }
        confirmText={isCustomerRow(confirmUnlink) ? "Bağı Kopar" : "Çıkar"}
        cancelText="İptal"
        isLoading={unlinkLoading || !!customersSource?.unlinkLoading}
      />
    </>
  );

  return {
    isDealerFirm,
    visibleFirms,
    isLoading: belowFirmsIsLoading,
    isFetching: belowFirmsIsFetching,
    sendableQuota,
    sendableFetching,
    busyIds,
    parentBusy,
    isCustomerRow,
    canLink,
    canLinkCustomer,
    canSendQuotaFor,
    canUnlinkFor,
    canSendAny: canSendQuota || canSendCustomerQuota,
    canUnlinkAny: canUnlink || canUnlinkCustomer,
    openSendQuota: (row) => setQuotaDrawer({ firm: row, direction: "toSub" }),
    openTakeBackQuota: (row) => setQuotaDrawer({ firm: row, direction: "toParent" }),
    askUnlink: setConfirmUnlink,
    linkLabel,
    renderLinkButton,
    renderAddButtons,
    renderLayer,
  };
}
